import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";

const ease = [0.22, 1, 0.36, 1];
const PROJECT_TYPES = ["Website", "Platform / SaaS", "E-commerce", "AI System", "Automation", "Other"];
const BUDGETS = ["< 10k", "10k – 30k", "30k – 60k", "60k – 100k", "100k+"];

export default function ContactModal({ open, onClose }) {
  const panelRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    project_type: "",
    budget: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sentOk, setSentOk] = useState(false);
  const submittedRef = useRef(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // esc to close, focus trap, body + lenis lock
  useEffect(() => {
    if (!open) return undefined;
    // reset success flag when modal re-opens
    setSentOk(false);
    submittedRef.current = false;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab") {
        const f = panelRef.current?.querySelectorAll(
          'a,button,input,textarea,select,[tabindex]:not([tabindex="-1"])'
        );
        if (!f || !f.length) return;
        const list = Array.from(f).filter((el) => !el.disabled);
        const first = list[0];
        const last = list[list.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    if (window.__lenis) window.__lenis.stop();
    const t = setTimeout(() => {
      panelRef.current?.querySelector("input,textarea,select,button")?.focus();
    }, 60);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
      clearTimeout(t);
    };
  }, [open, onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (sending || submittedRef.current) return; // guard against double-submit
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setSending(true);
    submittedRef.current = true;
    try {
      const source_page =
        typeof window !== "undefined" ? window.location.pathname + window.location.search : "";
      await api.post("/contact", { ...form, source_page });
      setSentOk(true);
      toast.success("Message sent. We'll reply within 24 hours.");
      setForm({ name: "", email: "", company: "", project_type: "", budget: "", message: "" });
    } catch (err) {
      // allow another attempt on failure
      submittedRef.current = false;
      toast.error(
        "We could not send the message right now. Please email us directly at hello@parabolum.digital",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[400] flex justify-end"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease }}
        >
          {/* backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/55"
            style={{ backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }}
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* panel */}
          <motion.aside
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Start a project"
            className="relative flex h-[100dvh] w-full flex-col bg-[#030303] text-white shadow-float sm:max-w-[540px]"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.6, ease }}
          >
            <form onSubmit={submit} className="flex h-full flex-col">
              {/* SCROLLABLE BODY — native wheel scroll (Lenis ignores this region) */}
              <div
                data-lenis-prevent
                className="flex-1 overflow-y-auto overscroll-contain px-7 pb-10 pt-10 [-webkit-overflow-scrolling:touch] md:px-12"
              >
                <button
                  type="button"
                  onClick={onClose}
                  data-cursor="big"
                  className="absolute right-6 top-6 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-[#030303] text-white/70 transition-colors hover:border-white hover:text-white"
                  aria-label="Close"
                >
                  ✕
                </button>

                {sentOk ? (
                  <div data-testid="contact-success" className="flex h-full min-h-[60vh] flex-col justify-center">
                    <span className="label text-[#E4FF00]">Message received</span>
                    <h2 className="ff-display mt-5 text-4xl font-extrabold leading-[0.95] tracking-tight md:text-5xl">
                      Thank you.
                    </h2>
                    <p className="mt-4 max-w-sm text-[14px] leading-relaxed text-white/65">
                      We&apos;ve received your message and will reply within 24 hours — directly, no account managers.
                    </p>
                    <p className="mt-2 max-w-sm text-[13px] leading-relaxed text-white/45">
                      A confirmation has been sent to your inbox.
                    </p>
                    <div className="mt-8 flex items-center gap-3">
                      <button
                        type="button"
                        onClick={onClose}
                        data-cursor="big"
                        className="rounded-full border border-white/15 px-5 py-3 text-[14px] text-white/85 transition-colors hover:border-white"
                      >
                        Close
                      </button>
                      <a
                        href="mailto:hello@parabolum.digital"
                        data-cursor="big"
                        className="rounded-full bg-[#E4FF00] px-5 py-3 text-[14px] font-semibold text-black"
                      >
                        hello@parabolum.digital
                      </a>
                    </div>
                  </div>
                ) : (
                <>
                <span className="label text-white/40">Start a project</span>
                <h2 className="ff-display mt-5 text-4xl font-extrabold leading-[0.95] tracking-tight md:text-5xl">
                  Let&apos;s build
                  <br />
                  <span className="text-[#E4FF00]">something exceptional.</span>
                </h2>
                <p className="mt-4 max-w-sm text-[14px] leading-relaxed text-white/55">
                  Have an idea, project or question? We&apos;d love to hear from you. We reply within 24 hours — directly, no account managers.
                </p>

                <div className="mt-8 space-y-5">
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <Field label="Name *">
                      <input value={form.name} onChange={set("name")} placeholder="Jane Doe" className="studio-input" disabled={sending} />
                    </Field>
                    <Field label="Email *">
                      <input type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" className="studio-input" disabled={sending} />
                    </Field>
                  </div>
                  <Field label="Company">
                    <input value={form.company} onChange={set("company")} placeholder="Company (optional)" className="studio-input" disabled={sending} />
                  </Field>
                  <Field label="Project type">
                    <div className="flex flex-wrap gap-2">
                      {PROJECT_TYPES.map((t) => (
                        <button
                          type="button"
                          key={t}
                          disabled={sending}
                          onClick={() => setForm((f) => ({ ...f, project_type: t }))}
                          className={`rounded-full border px-3.5 py-2 text-[13px] transition-colors disabled:opacity-50 ${
                            form.project_type === t
                              ? "border-[#E4FF00] bg-[#E4FF00] text-black"
                              : "border-white/15 text-white/70 hover:border-white/40"
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Budget">
                    <div className="flex flex-wrap gap-2">
                      {BUDGETS.map((b) => (
                        <button
                          type="button"
                          key={b}
                          disabled={sending}
                          onClick={() => setForm((f) => ({ ...f, budget: b }))}
                          className={`rounded-full border px-3.5 py-2 text-[13px] transition-colors disabled:opacity-50 ${
                            form.budget === b
                              ? "border-[#E4FF00] bg-[#E4FF00] text-black"
                              : "border-white/15 text-white/70 hover:border-white/40"
                          }`}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Message *">
                    <textarea rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about the project…" className="studio-input resize-none" disabled={sending} />
                  </Field>
                </div>

                <div className="mt-8 flex items-center justify-between text-[13px] text-white/50">
                  <a href="mailto:hello@parabolum.digital" className="link-underline">
                    hello@parabolum.digital
                  </a>
                  <span>Response in 24h</span>
                </div>
                </>
                )}
              </div>

              {/* STICKY SUBMIT FOOTER — hidden once sent (success view has its own close action) */}
              {!sentOk && (
                <div className="sticky bottom-0 border-t border-white/[0.06] bg-[#030303] px-7 pb-[max(20px,env(safe-area-inset-bottom))] pt-5 md:px-12">
                  <button
                    type="submit"
                    disabled={sending}
                    data-cursor="big"
                    data-testid="contact-submit"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[#E4FF00] px-6 py-4 text-[15px] font-semibold text-black transition-opacity disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {sending ? "Sending…" : "Send message"}
                  </button>
                </div>
              )}
            </form>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label mb-2 block text-white/40">{label}</span>
      {children}
    </label>
  );
}
